/*
 * CAPTURE-OWNED MODULE
 * DocTypeEditModal — shared modal for editing a document type (iim_document_types)
 * via PATCH /document-types/:id. Used by both the Settings page and the studio
 * (DocTypeSelector "Edit type") so the two edit experiences are identical.
 *
 * Edits the DOCUMENT TYPE itself (affects all documents of that type), not a
 * single template. The key is immutable and never sent. System types are
 * editable (Track 1 widened the server allow-list); only the key stays frozen.
 *
 * `fields` controls which inputs render so the same component can show the full
 * Settings set or just department+category in the studio.
 *
 * The `visibility` field (Track 2a) manages the doc type's ADDITIONAL
 * department-visibility set (read-only grant: documents of this type become
 * visible to these departments too). It loads/saves via its own GET/PUT, not the
 * doc-type PATCH payload. The type's primary department is excluded from the
 * options (its visibility already flows through the document's department).
 */
import React, { useState, useEffect } from 'react';
import { captureAPI } from '../../services/api';
import DepartmentSelector from './DepartmentSelector';
import './DocTypeEditModal.css';

const DEFAULT_CATEGORIES = ['Financial', 'Procurement', 'Legal', 'HR', 'General'];
const ALL_FIELDS = ['label', 'department', 'category', 'visibility', 'detection_phrases', 'is_active'];

export default function DocTypeEditModal({
  docType,
  departments = [],
  fields = ALL_FIELDS,
  categories = DEFAULT_CATEGORIES,
  note,
  onClose,
  onSaved,
}) {
  const [form, setForm] = useState({
    label: docType.label || '',
    primary_dept_key: docType.primary_dept_key || '',
    category: docType.category || 'General',
    detection_phrases: (docType.detection_phrases || []).join(', '),
    is_active: docType.is_active,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const show = (f) => fields.includes(f);

  // Additional-visibility set (Track 2a). Loaded lazily only when the field shows.
  const [visibilityKeys, setVisibilityKeys] = useState([]);
  const [visibilityLoading, setVisibilityLoading] = useState(false);

  useEffect(() => {
    if (!show('visibility') || !docType.id) return;
    let cancelled = false;
    (async () => {
      setVisibilityLoading(true);
      try {
        const res = await captureAPI.getDocumentTypeDepartments(docType.id);
        if (!cancelled) setVisibilityKeys(res?.departments || []);
      } catch {
        if (!cancelled) setVisibilityKeys([]);
      } finally {
        if (!cancelled) setVisibilityLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [docType.id]);

  const toggleVisibility = (key) =>
    setVisibilityKeys((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      // Build the payload from ONLY the visible fields, so a compact (studio)
      // editor never clobbers fields it doesn't show. Key is never sent.
      const payload = {};
      if (show('label')) payload.label = form.label.trim();
      if (show('department')) payload.primary_dept_key = form.primary_dept_key || null;
      if (show('category')) payload.category = form.category;
      if (show('detection_phrases')) {
        payload.detection_phrases = form.detection_phrases
          .split(',')
          .map((p) => p.trim().toLowerCase())
          .filter(Boolean);
      }
      if (show('is_active')) payload.is_active = form.is_active;

      await captureAPI.updateDocumentType(docType.id, payload);
      // Save the additional-visibility set separately (its own PUT). Done AFTER
      // the PATCH so the server drops whatever the (possibly just-changed)
      // primary department is from the additional set.
      if (show('visibility')) {
        await captureAPI.setDocumentTypeDepartments(docType.id, visibilityKeys);
      }
      onSaved?.(payload);
      onClose?.();
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="dtem-backdrop" onClick={onClose}>
      <form className="dtem-modal" onClick={(e) => e.stopPropagation()} onSubmit={handleSave}>
        <div className="dtem-header">
          <h3>Edit Document Type</h3>
          <button type="button" className="dtem-close" onClick={onClose}>✕</button>
        </div>

        <p className="dtem-note">
          {note || <>Edits the <strong>“{docType.label}”</strong> document type — changes
            apply to all documents of this type.</>}
        </p>

        {docType.is_system && (
          <p className="dtem-system-note">
            <strong>System type.</strong> Built-in — editable, but the key is fixed.
          </p>
        )}

        {show('label') && (
          <div className="dtem-field">
            <label>Label</label>
            <input
              type="text"
              value={form.label}
              onChange={(e) => setForm((f) => ({ ...f, label: e.target.value }))}
              maxLength={100}
            />
          </div>
        )}

        <div className="dtem-field dtem-hint-row">
          <span className="dtem-hint">Key: <code>{docType.key}</code> (cannot change)</span>
        </div>

        {show('department') && (
          <div className="dtem-field dtem-field--prominent">
            <label>Department</label>
            <DepartmentSelector
              value={form.primary_dept_key}
              onChange={(key) => setForm((f) => ({ ...f, primary_dept_key: key || '' }))}
              departments={departments}
              label={null}
            />
            <span className="dtem-hint">Owning department for this document type (optional).</span>
          </div>
        )}

        {show('category') && (
          <div className="dtem-field">
            <label className="dtem-label-secondary">Category</label>
            <select
              value={form.category}
              onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}
            >
              {[...new Set([...categories, form.category])].map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>
        )}

        {show('visibility') && (
          <div className="dtem-field">
            <label className="dtem-label-secondary">Also visible to (departments)</label>
            <span className="dtem-hint">
              Documents of this type are visible to these departments in addition to
              their own. View-only — it does not let them move documents.
            </span>
            {visibilityLoading ? (
              <span className="dtem-hint">Loading…</span>
            ) : (
              <div className="dtem-visibility-list">
                {departments
                  .filter((d) => d.is_active !== false && d.key !== form.primary_dept_key)
                  .map((d) => (
                    <label key={d.key} className="dtem-visibility-item">
                      <input
                        type="checkbox"
                        checked={visibilityKeys.includes(d.key)}
                        onChange={() => toggleVisibility(d.key)}
                      />
                      {' '}{d.label}
                    </label>
                  ))}
                {departments.filter((d) => d.is_active !== false && d.key !== form.primary_dept_key).length === 0 && (
                  <span className="dtem-hint">No other departments available.</span>
                )}
              </div>
            )}
          </div>
        )}

        {show('detection_phrases') && (
          <div className="dtem-field">
            <label>Detection Keywords (comma-separated)</label>
            <input
              type="text"
              value={form.detection_phrases}
              onChange={(e) => setForm((f) => ({ ...f, detection_phrases: e.target.value }))}
              placeholder="keyword1, keyword2, keyword3"
            />
            <span className="dtem-hint">Drives automatic classification.</span>
          </div>
        )}

        {show('is_active') && (
          <div className="dtem-field dtem-field--inline">
            <label>
              <input
                type="checkbox"
                checked={form.is_active}
                onChange={(e) => setForm((f) => ({ ...f, is_active: e.target.checked }))}
              />
              {' '}Active
            </label>
          </div>
        )}

        {error && <p className="dtem-error">{error}</p>}

        <div className="dtem-actions">
          <button type="button" className="dtem-btn dtem-btn-secondary" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button type="submit" className="dtem-btn dtem-btn-primary" disabled={saving}>
            {saving ? 'Saving…' : 'Save Changes'}
          </button>
        </div>
      </form>
    </div>
  );
}
